/**
 * VMD门店图片上传 & 点评 API
 */
import request from '@/utils/request'
import { applicationName } from '@/utils/const'

const base = `${applicationName}/web/vmd`
const reviewBase = `${applicationName}/web/vmd/review`

// ==================== 图片上传 ====================

/**
 * 查询上传人信息
 * @param {string} code 工号/姓名/手机号
 */
export function queryUser(code) {
  return request({
    url: `${base}/queryUser`,
    method: 'get',
    params: { code }
  })
}

/**
 * 企业微信OAuth自动解析用户身份
 * @param {string} code 企业微信OAuth回调的code参数
 * @returns { id, name, phone, isNull }
 */
export function resolveWxUser(code) {
  return request({
    url: `${base}/resolveWxUser`,
    method: 'get',
    params: { code }
  })
}

/**
 * 检查分块是否已存在（去重）
 * @param {string} uploadId 上传唯一ID
 * @param {number} index 分块序号
 * @param {string} deptName 部门名称（可选，精确查找）
 */
export function checkChunk(uploadId, index, deptName) {
  return request({
    url: `${base}/checkChunk`,
    method: 'get',
    params: { uploadId, index, deptName }
  })
}

/**
 * ★ 断点续传 — 查询服务端已存在的所有分块序号（微信方案）
 * 重传前调用，返回已上传的分块列表，前端仅补传缺失的分块。
 * @param {string} uploadId 上传唯一ID
 * @param {string} storagePath 存储路径（可选，精确查找）
 * @returns { chunks: [0, 1, 2, ...] }
 */
export function checkChunks(uploadId, storagePath) {
  return request({
    url: `${base}/checkChunks`,
    method: 'get',
    params: { uploadId, storagePath }
  })
}

/**
 * ★ 秒传检测 — 按文件SHA-256查询是否已有相同内容的文件（微信方案）
 * 前端计算文件哈希后调用，若返回 exists:true 则直接跳过上传。
 * @param {string} hash 文件SHA-256十六进制字符串
 * @returns { exists: true, url: "xxx" } 或 { exists: false }
 */
export function checkByMd5(hash) {
  return request({
    url: `${base}/checkByMd5`,
    method: 'get',
    params: { hash }
  })
}

/**
 * 上传单个分块
 * @param {FormData} formData 包含 chunk, index, total, filename, uploadId, userCode, userName, deptName
 */
export function uploadChunk(formData) {
  return request({
    url: `${base}/uploadChunk`,
    method: 'post',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000 // 单块超时2分钟
  })
}

/**
 * ★ 显式合并通知 — 所有分块上传完成后调用，触发服务端校验与合并
 *
 * 设计目的：解决 uploadChunk 隐式合并时"最后一个到达的chunk发现缺失却返回 success"
 * 导致前端误判为成功的BUG。改为前端主动通知"我发完了"，服务端精确检查并合并。
 *
 * @param {Object} params { uploadId, total, filename, userCode, userName, deptName, userPhone?, storagePath? }
 * @returns { success, merged, url?, missingChunks?, received?, total?, message }
 */
export function notifyComplete(params) {
  return request({
    url: `${base}/notifyComplete`,
    method: 'post',
    params
  })
}

// ==================== 图片点评 ====================

/**
 * 搜索已上传图片
 * @param {Object} params { dept, startDate, endDate, keyword, page, size }
 */
export function searchImages(params) {
  return request({
    url: `${reviewBase}/images`,
    method: 'get',
    params
  })
}

/**
 * 获取分公司列表
 */
export function getBranchCompanyList() {
  return request({
    url: `${reviewBase}/branchCompanies`,
    method: 'get'
  })
}

/**
 * 获取区域列表
 * @param {string} branchCompany 分公司（可选，筛选该分公司下的区域）
 */
export function getRegionList(branchCompany) {
  return request({
    url: `${reviewBase}/regions`,
    method: 'get',
    params: branchCompany ? { branchCompany } : {}
  })
}

/**
 * 获取部门列表
 * @param {string} region 区域（可选，筛选该区域下的部门）
 */
export function getDeptList(region) {
  return request({
    url: `${reviewBase}/depts`,
    method: 'get',
    params: region ? { region } : {}
  })
}

/**
 * 获取图片的所有点评
 * @param {string} imageId 图片ID
 */
export function getComments(imageId) {
  return request({
    url: `${reviewBase}/comments`,
    method: 'get',
    params: { imageId }
  })
}

/**
 * 添加点评
 * @param {Object} data 点评数据
 */
export function addComment(data) {
  return request({
    url: `${reviewBase}/comments`,
    method: 'post',
    data
  })
}

/**
 * 删除点评
 * @param {string} id 点评ID
 */
export function deleteComment(id) {
  return request({
    url: `${reviewBase}/comments/${id}`,
    method: 'delete'
  })
}

/**
 * 点赞
 * @param {string} id 点评ID
 */
export function likeComment(id) {
  return request({
    url: `${reviewBase}/comments/${id}/like`,
    method: 'post'
  })
}
